import { A } from '@solidjs/router';
import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { AdminUserRow as UserRow } from '../lib/admin';
import { AdminUserActions } from './AdminUserActions';
import { DataTable, type DataTableColumn } from './DataTable';
import { StatusPill } from './StatusPill';

// The admin user registry as a dense data table (spec §3.15). Presenter — the
// panel owns the accumulated rows + pagination; this paints one row per user,
// its login linking to the person's page (`/admin/users/:id`, #5727), and
// `<AdminUserActions/>` owns each row's approve / suspend / grant.
export function AdminUsersTable(props: {
  rows: readonly UserRow[];
  token: string | null;
  onMutated: () => void;
}): JSX.Element {
  const approved = (row: UserRow): boolean => row.approved_at_iso !== null;
  const columns: readonly DataTableColumn<UserRow>[] = [
    {
      key: 'user',
      rowHeader: true,
      header: t('admin.col_user'),
      cell: (row) => (
        <div class="admin-row__stack">
          <A class="data-table__mono" href={`/admin/users/${encodeURIComponent(row.id)}`}>
            {row.login}
          </A>
          <Show when={row.email}>{(email) => <span class="admin-row__meta">{email()}</span>}</Show>
        </div>
      ),
    },
    {
      key: 'status',
      header: t('admin.users_status_label'),
      cell: (row) => (
        <StatusPill tone={approved(row) ? 'success' : 'warning'}>
          {approved(row) ? t('admin.user_approved') : t('admin.user_waitlisted')}
        </StatusPill>
      ),
    },
    {
      key: 'memberships',
      header: t('admin.col_memberships'),
      cell: (row) => (
        <Show when={row.memberships.length > 0}>
          <ul class="admin-user-row__memberships">
            <For each={row.memberships}>
              {(m) => (
                <li>
                  {m.github_login} · {m.role}
                </li>
              )}
            </For>
          </ul>
        </Show>
      ),
    },
    {
      key: 'actions',
      header: t('admin.col_actions'),
      align: 'end',
      cell: (row) => <AdminUserActions row={row} token={props.token} onMutated={props.onMutated} />,
    },
  ];

  return (
    <DataTable
      label={t('admin.users_table_label')}
      columns={columns}
      rows={props.rows}
      rowKey={(row) => row.id}
    />
  );
}
